import { Share as ShareIcon } from "@mui/icons-material";
import React from "react";
import { UserResponse } from "../../api/authApi";
import { UserRoles } from "../../constants/roles";
import {
  hasSubscriptionAccess,
  SubscriptionLevel,
} from "../../constants/subscriptionLevels";
import {
  AbsenceIcon,
  AssignmentIndIcon,
  AuditIcon,
  BusinessIcon,
  ClaimsIcon,
  DashboardIcon,
  DetentionIcon,
  DocumentsIcon,
  DotInspectionsIcon,
  IntegrationIcon,
  MaintenanceIcon,
  PaymentIcon,
  PtiIcon,
  ReportsIcon,
  SafetyBoardIcon,
  StoreIcon,
  TrailersIcon,
  TruckIcon,
  UserIcon,
  VehiclesIcon,
} from "../common/pageIcons";

export type NavHubId = "fleet" | "safety" | "accounting" | "admin";

export const HUB_PATHS: Record<NavHubId, string> = {
  fleet: "/hubs/fleet",
  safety: "/hubs/safety",
  accounting: "/hubs/accounting",
  admin: "/hubs/admin",
};

export const HUB_META: Record<
  NavHubId,
  { title: string; subtitle: string }
> = {
  fleet: {
    title: "Fleet",
    subtitle: "Shops, equipment, inspections, and maintenance",
  },
  safety: {
    title: "Safety",
    subtitle: "Documents, claims, DOT inspections, people, and assignments",
  },
  accounting: {
    title: "Accounting",
    subtitle: "Detention, lumper, and IFTA reporting",
  },
  admin: {
    title: "Admin",
    subtitle: "Subscriptions, absences, integrations, and audit",
  },
};

export interface MenuItemBase {
  text: string;
  type?: "label";
  path?: string;
  icon?: React.ReactNode;
  requiredLevel?: SubscriptionLevel;
}

const SHORTCUT_DESCRIPTIONS: Record<string, string> = {
  "/shops": "Preferred shops and vendor locations",
  "/vehicles": "Unit details, status, and vehicle records",
  "/trailers": "Trailer inventory and unit status",
  "/pti": "Pre-trip inspection records and compliance",
  "/maintenance": "Vehicle and trailer service records",
  "/documents": "Company, vehicle, trailer, and driver files",
  "/claims": "Track and manage claims",
  "/dot-inspections": "Track and manage DOT inspections",
  "/safety": "Safety incidents and follow-up",
  "/users": "People and account access",
  "/admin/driver-assignments": "Assign drivers to trucks",
  "/detention-lumpers": "Detention and lumper charges",
  "/reports/ifta": "Fuel tax reporting",
  "/subscriptions": "Plan, billing, and vehicle count",
  "/admin/absences": "Time off requests and balances",
  "/admin/integrations": "Connected accounts and services",
  "/admin/share-links": "Public tracking links",
  "/audit": "Account activity history",
};

export function getShortcutDescription(item: MenuItemBase): string {
  if (item.text === "Drivers") return "Driver accounts and profiles";
  if (item.text === "Users") return "User accounts and access";
  if (item.path && SHORTCUT_DESCRIPTIONS[item.path]) {
    return SHORTCUT_DESCRIPTIONS[item.path];
  }
  return `Open ${item.text}`;
}

export function isNavPathActive(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`);
}

export function pruneEmptySectionLabels(items: MenuItemBase[]): MenuItemBase[] {
  return items.filter((item, index) => {
    if (item.type !== "label") return true;
    for (let i = index + 1; i < items.length; i++) {
      if (items[i].type === "label") return false;
      return true;
    }
    return false;
  });
}

export function isSectionActive(
  items: MenuItemBase[],
  labelIndex: number,
  pathname: string,
): boolean {
  const label = items[labelIndex];
  if (label.path && pathname === label.path) return true;
  for (let i = labelIndex + 1; i < items.length; i++) {
    if (items[i].type === "label") break;
    const path = items[i].path;
    if (path && isNavPathActive(pathname, path)) return true;
  }
  return false;
}

export type NavGroup =
  | { kind: "item"; item: MenuItemBase }
  | { kind: "section"; label: MenuItemBase; items: MenuItemBase[] };

export function isSectionGroupActive(
  label: MenuItemBase,
  items: MenuItemBase[],
  pathname: string,
): boolean {
  if (label.path && pathname === label.path) return true;
  return items.some(
    (item) => item.path != null && isNavPathActive(pathname, item.path),
  );
}

export function groupNavItems(items: MenuItemBase[]): NavGroup[] {
  const groups: NavGroup[] = [];
  let current: Extract<NavGroup, { kind: "section" }> | null = null;

  for (const item of items) {
    if (item.type === "label") {
      current = { kind: "section", label: item, items: [] };
      groups.push(current);
      continue;
    }
    if (current) {
      current.items.push(item);
    } else {
      groups.push({ kind: "item", item });
    }
  }

  return groups;
}

export function getHubShortcuts(
  items: MenuItemBase[],
  hubPath: string,
): MenuItemBase[] {
  const start = items.findIndex(
    (item) => item.type === "label" && item.path === hubPath,
  );
  if (start === -1) return [];
  const shortcuts: MenuItemBase[] = [];
  for (let i = start + 1; i < items.length; i++) {
    if (items[i].type === "label") break;
    shortcuts.push(items[i]);
  }
  return shortcuts;
}

export function buildNavItems(user: UserResponse | null): MenuItemBase[] {
  const menuItems: MenuItemBase[] = [];

  if (user?.role === "SuperAdmin") {
    menuItems.push(
      {
        text: "Tenants",
        icon: <BusinessIcon />,
        path: "/tenants",
        requiredLevel: "Lite",
      },
      {
        text: "Subscriptions",
        icon: <PaymentIcon />,
        path: "/subscriptions",
        requiredLevel: "Lite",
      },
      {
        text: "Shops",
        icon: <StoreIcon />,
        path: "/shops",
        requiredLevel: "Lite",
      },
      {
        text: "Audit Log",
        icon: <AuditIcon />,
        path: "/audit",
        requiredLevel: "Lite",
      },
    );
  } else if (user?.role === "Admin") {
    menuItems.push(
      {
        text: "Dashboard",
        icon: <DashboardIcon />,
        path: "/dashboard",
        requiredLevel: "Lite",
      },
      { type: "label", text: "Fleet", path: HUB_PATHS.fleet },
      {
        text: "Shop",
        icon: <StoreIcon />,
        path: "/shops",
        requiredLevel: "Lite",
      },
      {
        text: "Vehicles",
        icon: <VehiclesIcon />,
        path: "/vehicles",
        requiredLevel: "Lite",
      },
      {
        text: "Trailers",
        icon: <TrailersIcon />,
        path: "/trailers",
        requiredLevel: "Lite",
      },
      {
        text: "Pre-Trip Inspections",
        icon: <PtiIcon />,
        path: "/pti",
        requiredLevel: "Standard",
      },
      {
        text: "Maintenance",
        icon: <MaintenanceIcon />,
        path: "/maintenance",
        requiredLevel: "Lite",
      },
      { type: "label", text: "Safety", path: HUB_PATHS.safety },
      {
        text: "Documents",
        icon: <DocumentsIcon />,
        path: "/documents",
        requiredLevel: "Lite",
      },
      {
        text: "Claims Board",
        icon: <ClaimsIcon />,
        path: "/claims",
        requiredLevel: "Lite",
      },
      {
        text: "DOT Inspections",
        icon: <DotInspectionsIcon />,
        path: "/dot-inspections",
        requiredLevel: "Lite",
      },
      {
        text: "Safety Board",
        icon: <SafetyBoardIcon />,
        path: "/safety",
        requiredLevel: "Lite",
      },
      {
        text: "Users",
        icon: <UserIcon />,
        path: "/users",
        requiredLevel: "Lite",
      },
      {
        text: "Driver Assignments",
        icon: <AssignmentIndIcon />,
        path: "/admin/driver-assignments",
        requiredLevel: "Standard",
      },
      { type: "label", text: "Accounting", path: HUB_PATHS.accounting },
      {
        text: "Detention / Lumper",
        icon: <DetentionIcon />,
        path: "/detention-lumpers",
        requiredLevel: "Lite",
      },
      {
        text: "IFTA Report",
        icon: <ReportsIcon />,
        path: "/reports/ifta",
        requiredLevel: "Pro",
      },
      { type: "label", text: "Admin", path: HUB_PATHS.admin },
      {
        text: "Subscriptions",
        icon: <PaymentIcon />,
        path: "/subscriptions",
        requiredLevel: "Lite",
      },
      {
        text: "Absences",
        icon: <AbsenceIcon />,
        path: "/admin/absences",
        requiredLevel: "Standard",
      },
      {
        text: "Integrations",
        icon: <IntegrationIcon />,
        path: "/admin/integrations",
        requiredLevel: "Lite",
      },
      {
        text: "Share Links",
        icon: <ShareIcon />,
        path: "/admin/share-links",
        requiredLevel: "Lite",
      },
      {
        text: "Audit Log",
        icon: <AuditIcon />,
        path: "/audit",
        requiredLevel: "Lite",
      },
    );
  } else if (
    user?.role === UserRoles.DISPATCHER ||
    user?.role === UserRoles.FLEET ||
    user?.role === UserRoles.RECRUITING ||
    user?.role === UserRoles.ACCOUNTING
  ) {
    menuItems.push(
      {
        text: "Dashboard",
        icon: <DashboardIcon />,
        path: "/dashboard",
        requiredLevel: "Lite",
      },
      { type: "label", text: "Fleet", path: HUB_PATHS.fleet },
      {
        text: "Shop",
        icon: <StoreIcon />,
        path: "/shops",
        requiredLevel: "Lite",
      },
      {
        text: "Vehicles",
        icon: <VehiclesIcon />,
        path: "/vehicles",
        requiredLevel: "Lite",
      },
      {
        text: "Trailers",
        icon: <TrailersIcon />,
        path: "/trailers",
        requiredLevel: "Lite",
      },
      ...(user?.role === UserRoles.FLEET
        ? [
            {
              text: "Pre-Trip Inspections",
              icon: <PtiIcon />,
              path: "/pti",
              requiredLevel: "Standard" as SubscriptionLevel,
            },
            {
              text: "Maintenance",
              icon: <MaintenanceIcon />,
              path: "/maintenance",
              requiredLevel: "Lite" as SubscriptionLevel,
            },
          ]
        : []),
      { type: "label", text: "Safety", path: HUB_PATHS.safety },
      {
        text: "Documents",
        icon: <DocumentsIcon />,
        path: "/documents",
        requiredLevel: "Lite",
      },
      {
        text: "Claims Board",
        icon: <ClaimsIcon />,
        path: "/claims",
        requiredLevel: "Lite",
      },
      {
        text: "DOT Inspections",
        icon: <DotInspectionsIcon />,
        path: "/dot-inspections",
        requiredLevel: "Lite",
      },
      {
        text: "Drivers",
        icon: <UserIcon />,
        path: "/users",
        requiredLevel: "Lite",
      },
      {
        text: "Driver Assignments",
        icon: <AssignmentIndIcon />,
        path: "/admin/driver-assignments",
        requiredLevel: "Standard",
      },
    );

    if (
      user?.role === UserRoles.DISPATCHER ||
      user?.role === UserRoles.ACCOUNTING
    ) {
      menuItems.push(
        { type: "label", text: "Accounting", path: HUB_PATHS.accounting },
        {
          text: "Detention / Lumper",
          icon: <DetentionIcon />,
          path: "/detention-lumpers",
          requiredLevel: "Lite",
        },
      );
    }

    if (user?.role === UserRoles.ACCOUNTING) {
      menuItems.push({
        text: "IFTA Report",
        icon: <ReportsIcon />,
        path: "/reports/ifta",
        requiredLevel: "Pro",
      });
    }
  } else if (
    user?.role === UserRoles.SAFETY ||
    user?.role === UserRoles.IFTA_ADMIN
  ) {
    menuItems.push(
      {
        text: "Dashboard",
        icon: <DashboardIcon />,
        path: "/dashboard",
        requiredLevel: "Lite",
      },
      { type: "label", text: "Fleet", path: HUB_PATHS.fleet },
      {
        text: "Shop",
        icon: <StoreIcon />,
        path: "/shops",
        requiredLevel: "Lite",
      },
      {
        text: "Vehicles",
        icon: <VehiclesIcon />,
        path: "/vehicles",
        requiredLevel: "Lite",
      },
      {
        text: "Trailers",
        icon: <TrailersIcon />,
        path: "/trailers",
        requiredLevel: "Lite",
      },
      { type: "label", text: "Safety", path: HUB_PATHS.safety },
      {
        text: "Documents",
        icon: <DocumentsIcon />,
        path: "/documents",
        requiredLevel: "Lite",
      },
      {
        text: "Claims Board",
        icon: <ClaimsIcon />,
        path: "/claims",
        requiredLevel: "Lite",
      },
      {
        text: "DOT Inspections",
        icon: <DotInspectionsIcon />,
        path: "/dot-inspections",
        requiredLevel: "Lite",
      },
      {
        text: "Safety Board",
        icon: <SafetyBoardIcon />,
        path: "/safety",
        requiredLevel: "Lite",
      },
      {
        text: "Drivers",
        icon: <UserIcon />,
        path: "/users",
        requiredLevel: "Lite",
      },
      {
        text: "Driver Assignments",
        icon: <AssignmentIndIcon />,
        path: "/admin/driver-assignments",
        requiredLevel: "Standard",
      },
      { type: "label", text: "Accounting", path: HUB_PATHS.accounting },
      {
        text: "IFTA Report",
        icon: <ReportsIcon />,
        path: "/reports/ifta",
        requiredLevel: "Pro",
      },
    );
  } else if (user?.role === "TruckDriver") {
    menuItems.push({
      text: "Truck Driver Portal",
      icon: <TruckIcon />,
      path: "/truck-driver",
      requiredLevel: "Standard",
    });
  }

  return menuItems;
}

export function getVisibleNavItems(
  user: UserResponse | null,
  subscriptionLevel: string | null,
): MenuItemBase[] {
  return pruneEmptySectionLabels(
    buildNavItems(user).filter((item) => {
      if (item.type === "label") return true;
      const required = item.requiredLevel ?? "Lite";
      return hasSubscriptionAccess(subscriptionLevel, required);
    }),
  );
}
